import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TopNav from './TopNav';
import api from '../services/api';

interface TradingUniverseStock {
  symbol: string;
  sector: string;
  industry: string;
  lastPrice: number;
  spreadBps: number;
  adv20Shares: number;
  dollarAdv20: number;
  marketCap: number;
  freeFloat: number;
  liquidityScore: number;
  volatilityScore: number;
  sentimentScore: number;
  technicalScore: number;
  compositeScore: number;
  sectorPercentile: number;
  regimeLabel: 'risk_off' | 'risk_on' | 'neutral' | 'UNKNOWN';
  reasons: string[];
  freshOrSticky: 'fresh' | 'sticky';
  cooldownFlag: boolean;
  buyScore?: number;
  sellScore?: number;
  // Enhanced scoring fields (v2.0)
  activityScore?: number;
  buzzScore?: number;
  earningsZone?: 'FAR' | 'APPROACH' | 'IMMINENT' | 'BLACKOUT' | 'POST_EARNINGS' | 'UNKNOWN';
  daysToEarnings?: number;
  earningsPositionMultiplier?: number;
  // New enhanced tracking fields (v3.0)
  rawCompositeScore?: number;
  smoothedCompositeScore?: number;
  scoreMomentum?: number;
  relativeStrengthVsSPY?: number;
  consecutiveSessionsInUniverse?: number;
  sectorStrength?: number;
  // Timestamp fields
  addedAt?: string; // UTC timestamp
  addedAtET?: string; // ET formatted timestamp
}

interface RegimeSignals {
  vix: number;
  spy_ma_20_pct: number;
  spy_ma_50_pct: number;
  regime_label: 'risk_off' | 'risk_on' | 'neutral' | 'UNKNOWN';
}

interface RegimeConfig {
  weights: {
    volatility: number;
    spyTrend: number;
    breadth: number;
    putCall: number;
    creditSpread: number;
    economic: number;
  };
  thresholds: {
    risk_off: number;
    risk_on: number;
  };
  source: string;
}

interface EnhancedRegimeSignals {
  regime_label: 'risk_off' | 'risk_on' | 'neutral' | 'UNKNOWN';
  regime_score: number;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  stability?: {
    isStable: boolean;
    minutesSinceLastSwitch?: number;
    recentLabels: string[];
  };
  errors?: Array<{
    code: string;
    indicator: string;
    message: string;
  }>;
  behavior?: {
    max_new_entries: number;
    position_size_multiplier: number;
    exits_active: boolean;
    monitoring: string;
  };
  indicators: {
    volatility: {
      rv20: number;
      zScore: number;
      percentile: number;
      state: 'LOW' | 'NORMAL' | 'HIGH' | 'STRESS';
      score: number;
      vxxStress?: boolean;
      vxxCurrent?: number;
      vxxMa20?: number;
    } | null;
    spyTrend: {
      price: number;
      ma20: number;
      ma50: number;
      ma200: number;
      aboveMa20: boolean;
      aboveMa50: boolean;
      aboveMa200: boolean;
      goldenCross: boolean;
      deathCross: boolean;
      trend: 'STRONG_UP' | 'UP' | 'NEUTRAL' | 'DOWN' | 'STRONG_DOWN';
      score: number;
    };
    breadth: {
      rspSpyRatio?: number;
      rspSpyMa20?: number;
      rspSpyMa50?: number;
      ratioSlope?: number;
      zScore?: number;
      advanceDeclineRatio: number;
      percentAbove50dma: number;
      percentAbove200dma: number;
      newHighsLows: number;
      signal: 'STRONG_BULLISH' | 'BULLISH' | 'NEUTRAL' | 'BEARISH' | 'STRONG_BEARISH';
      score: number;
    };
    putCall?: {
      ratio: number;
      equityRatio: number;
      indexRatio: number;
      ma5: number;
      signal: 'EXTREME_FEAR' | 'FEAR' | 'NEUTRAL' | 'GREED' | 'EXTREME_GREED';
      score: number;
    };
    creditSpread: {
      hygPrice: number;
      lqdPrice: number;
      spreadRatio: number;
      spreadChange5d?: number;
      zScore?: number;
      signal: 'RISK_ON' | 'NEUTRAL' | 'RISK_OFF' | 'STRESS';
      score: number;
    };
    economic: {
      unemploymentRate: number;
      yieldCurve: number;
      fedFundsRate: number;
      signal: 'EXPANSION' | 'NEUTRAL' | 'CONTRACTION' | 'RECESSION_WARNING';
      score: number;
    };
  };
  signals: string[];
  timestamp: string;
}

interface QAResult {
  ruleId: string;
  passed: boolean;
  actual: any;
  expected: any;
  message: string;
  severity: string;
  timestamp: Date;
}

interface TradingUniverseData {
  universe: TradingUniverseStock[];
  regime: RegimeSignals;
  enhancedRegime?: EnhancedRegimeSignals | null;
  regimeConfig?: RegimeConfig | null;
  qaResults: QAResult[];
  metadata: {
    totalStocks: number;
    stickySeats: number;
    freshSeats: number;
    lastUpdated: string;
    lastUpdatedET?: string;
    nextBuildTime?: string;
    buildSchedule?: string[];
  };
}

const TradingUniverse: React.FC = () => {
  const { } = useAuth();
  const [data, setData] = useState<TradingUniverseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Persist active tab to localStorage
  const getInitialTab = (): 'universe' | 'regime' | 'qa' => {
    const savedTab = localStorage.getItem('tradingUniverseActiveTab') as 'universe' | 'regime' | 'qa' | null;
    if (savedTab && ['universe', 'regime', 'qa'].includes(savedTab)) {
      return savedTab;
    }
    return 'universe';
  };
  
  const [activeTab, setActiveTab] = useState<'universe' | 'regime' | 'qa'>(getInitialTab());
  
  const handleTabChange = (tab: 'universe' | 'regime' | 'qa') => {
    setActiveTab(tab);
    localStorage.setItem('tradingUniverseActiveTab', tab);
  };
  const [sortBy, setSortBy] = useState<'compositeScore' | 'sector' | 'freshOrSticky' | 'buyScore' | 'momentum' | 'relativeStrength' | 'sectorPercentile'>('compositeScore');
  const [filterBy, setFilterBy] = useState<'all' | 'fresh' | 'sticky' | 'cooldown'>('all');
  const [policyVersion, setPolicyVersion] = useState<string>('3.0.0');
  const [expandedStock, setExpandedStock] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [response, policyResponse] = await Promise.all([
        api.get('/selectors/trading-universe'),
        api.get('/admin/policy')
      ]);
      setData(response.data.data);
      setPolicyVersion(policyResponse.data.data.version || '2.9.0');
    } catch (err) {
      console.error('Failed to fetch Trading Universe data:', err);
      setError('Failed to load Trading Universe data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getRegimeColor = (regime: string) => {
    switch (regime) {
      case 'risk_on': return 'text-green-400';
      case 'risk_off': return 'text-red-400';
      case 'neutral': return 'text-yellow-400';
      default: return 'text-neutral-400';
    }
  };

  const _getRegimeIcon = (regime: string) => {
    switch (regime) {
      case 'risk_on': return 'UP';
      case 'risk_off': return 'DOWN';
      case 'neutral': return 'FLAT';
      default: return 'UNKNOWN';
    }
  };
  void _getRegimeIcon; // Reserved for future use

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-neutral-400';
    }
  };

  const filteredStocks = data?.universe.filter((stock: TradingUniverseStock) => {
    if (filterBy === 'all') return true;
    if (filterBy === 'cooldown') return stock.cooldownFlag;
    return stock.freshOrSticky === filterBy;
  }).sort((a: TradingUniverseStock, b: TradingUniverseStock) => {
    switch (sortBy) {
      case 'compositeScore':
        return b.compositeScore - a.compositeScore;
      case 'sector':
        return a.sector.localeCompare(b.sector);
      case 'freshOrSticky':
        return a.freshOrSticky.localeCompare(b.freshOrSticky);
      case 'buyScore':
        const aScore = a.buyScore ?? 0;
        const bScore = b.buyScore ?? 0;
        return bScore - aScore;
      case 'momentum':
        return (b.scoreMomentum ?? 0) - (a.scoreMomentum ?? 0);
      case 'relativeStrength':
        return (b.relativeStrengthVsSPY ?? 0.5) - (a.relativeStrengthVsSPY ?? 0.5);
      case 'sectorPercentile':
        return (b.sectorPercentile ?? 0) - (a.sectorPercentile ?? 0);
      default:
        return 0;
    }
  }) || [];

  // Calculate summary statistics
  const avgMomentum = filteredStocks.length > 0 
    ? filteredStocks.reduce((sum: number, s: TradingUniverseStock) => sum + (s.scoreMomentum ?? 0), 0) / filteredStocks.length 
    : 0;
  const cooldownCount = data?.universe.filter((s: TradingUniverseStock) => s.cooldownFlag).length ?? 0;
  const avgRS = filteredStocks.length > 0
    ? filteredStocks.reduce((sum: number, s: TradingUniverseStock) => sum + (s.relativeStrengthVsSPY ?? 0.5), 0) / filteredStocks.length
    : 0.5;

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            <span className="ml-3 text-neutral-400">Loading Trading Universe...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Trading Universe Selector (Policy {policyVersion})</h1>
            <p className="text-neutral-400">
              Sophisticated multi-factor stock selection ({data?.metadata.totalStocks || 0} stocks) • Last updated {data?.metadata.lastUpdatedET || (data?.metadata.lastUpdated ? new Date(data.metadata.lastUpdated).toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) + ' ET' : 'Unknown')}
              {data?.metadata.nextBuildTime && (
                <> • Next build {new Date(data.metadata.nextBuildTime).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' }) + ' ET'}</>
              )}
            </p>
          </div>

          {/* Summary Cards - Row 1 */}
          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
                <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-cyan-500">
                  <p className="text-xs text-neutral-400 uppercase tracking-wide">Total Universe</p>
                  <p className="text-2xl font-bold text-white mt-1">{data.metadata.totalStocks}</p>
                  <p className="text-xs text-neutral-500 mt-1">stocks</p>
                </div>
                
                <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-blue-500">
                  <p className="text-xs text-neutral-400 uppercase tracking-wide">Sticky Seats</p>
                  <p className="text-2xl font-bold text-blue-400 mt-1">{data.metadata.stickySeats}</p>
                  <p className="text-xs text-blue-400/60 mt-1">persistent</p>
                </div>
                
                <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-green-500">
                  <p className="text-xs text-neutral-400 uppercase tracking-wide">Fresh Seats</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">{data.metadata.freshSeats}</p>
                  <p className="text-xs text-green-400/60 mt-1">new entrants</p>
                </div>
                
                <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-orange-500">
                  <p className="text-xs text-neutral-400 uppercase tracking-wide">In Cooldown</p>
                  <p className="text-2xl font-bold text-orange-400 mt-1">{cooldownCount}</p>
                  <p className="text-xs text-orange-400/60 mt-1">re-entry blocked</p>
                </div>
                
                <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-purple-500">
                  <p className="text-xs text-neutral-400 uppercase tracking-wide">Avg Momentum</p>
                  <p className={`text-2xl font-bold mt-1 ${avgMomentum > 0 ? 'text-green-400' : avgMomentum < 0 ? 'text-red-400' : 'text-neutral-400'}`}>
                    {avgMomentum > 0 ? '+' : ''}{(avgMomentum * 100).toFixed(2)}%
                  </p>
                  <p className="text-xs text-purple-400/60 mt-1">score trend</p>
                </div>
                
                <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-yellow-500">
                  <p className="text-xs text-neutral-400 uppercase tracking-wide">Avg RS vs SPY</p>
                  <p className={`text-2xl font-bold mt-1 ${avgRS > 0.55 ? 'text-green-400' : avgRS < 0.45 ? 'text-red-400' : 'text-neutral-400'}`}>
                    {(avgRS * 100).toFixed(1)}%
                  </p>
                  <p className="text-xs text-yellow-400/60 mt-1">relative strength</p>
                </div>
              </div>

              {/* Market Regime Banner */}
              <div className={`rounded-lg p-4 mb-6 border ${
                data.regime.regime_label === 'risk_on' ? 'bg-green-900/20 border-green-500/50' :
                data.regime.regime_label === 'risk_off' ? 'bg-red-900/20 border-red-500/50' :
                'bg-yellow-900/20 border-yellow-500/50'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`text-4xl ${getRegimeColor(data.regime.regime_label)}`}>
                      {data.regime.regime_label === 'risk_on' ? '>' : data.regime.regime_label === 'risk_off' ? '<' : '='}{data.regime.regime_label === 'risk_on' ? '>' : data.regime.regime_label === 'risk_off' ? '<' : '='}
                    </div>
                    <div>
                      <p className={`text-xl font-bold ${getRegimeColor(data.regime.regime_label)}`}>
                        {data.regime.regime_label.replace('_', '-').toUpperCase()} MODE
                      </p>
                      <p className="text-sm text-neutral-400">
                        VIX: {data.regime.vix ? data.regime.vix.toFixed(1) : 'N/A'} | SPY vs MA20: {data.regime.spy_ma_20_pct ? (data.regime.spy_ma_20_pct > 0 ? '+' : '') + data.regime.spy_ma_20_pct.toFixed(2) : '0.00'}%
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-neutral-500">Enhanced Regime Detection</p>
                    <p className="text-sm text-neutral-400">6 indicators active</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex space-x-4">
              <button
                onClick={() => handleTabChange('universe')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'universe'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Stock Universe
              </button>
              <button
                onClick={() => handleTabChange('regime')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'regime'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Market Regime
              </button>
              <button
                onClick={() => handleTabChange('qa')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'qa'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                QA Results
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'universe' && (
            <div className="bg-neutral-800 rounded-lg p-6">
              {/* Controls */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Sort by</label>
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      const value = e.target.value as typeof sortBy;
                      setSortBy(value);
                    }}
                    className="bg-neutral-700 text-white px-3 py-2 rounded-lg border border-neutral-600 focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="compositeScore">Composite Score</option>
                    <option value="buyScore">Buy Score</option>
                    <option value="momentum">Score Momentum</option>
                    <option value="relativeStrength">Relative Strength</option>
                    <option value="sectorPercentile">Sector Percentile</option>
                    <option value="sector">Sector</option>
                    <option value="freshOrSticky">Fresh/Sticky</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Filter by</label>
                  <select
                    value={filterBy}
                    onChange={(e) => {
                      const value = e.target.value as typeof filterBy;
                      setFilterBy(value);
                    }}
                    className="bg-neutral-700 text-white px-3 py-2 rounded-lg border border-neutral-600 focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="all">All Stocks ({data?.metadata.totalStocks || 0})</option>
                    <option value="fresh">Fresh Seats ({data?.metadata.freshSeats || 0})</option>
                    <option value="sticky">Sticky Seats ({data?.metadata.stickySeats || 0})</option>
                    <option value="cooldown">In Cooldown ({cooldownCount})</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={fetchData}
                    className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Refresh</span>
                  </button>
                </div>

                <div className="flex items-end ml-auto text-sm text-neutral-400">
                  Showing {filteredStocks.length} of {data?.metadata.totalStocks || 0} stocks
                </div>
              </div>

              {/* Stock Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-neutral-300 border-b border-neutral-700 bg-neutral-800/50">
                      <th className="py-3 px-3 sticky left-0 bg-neutral-800">Symbol</th>
                      <th className="py-3 px-2">Type</th>
                      <th className="py-3 px-2">Sector</th>
                      <th className="py-3 px-2">Price</th>
                      <th className="py-3 px-2">Composite</th>
                      <th className="py-3 px-2">Momentum</th>
                      <th className="py-3 px-2">RS vs SPY</th>
                      <th className="py-3 px-2">Sector %ile</th>
                      <th className="py-3 px-2">Sessions</th>
                      <th className="py-3 px-2">Earnings</th>
                      <th className="py-3 px-2">Scores</th>
                      <th className="py-3 px-2">Added At</th>
                      <th className="py-3 px-2">Reasons</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-700/50">
                    {filteredStocks.map((stock: TradingUniverseStock) => (
                      <React.Fragment key={stock.symbol}>
                        <tr 
                          className={`hover:bg-neutral-700/30 cursor-pointer transition-colors ${
                            stock.cooldownFlag ? 'bg-orange-900/10' : ''
                          } ${expandedStock === stock.symbol ? 'bg-neutral-700/50' : ''}`}
                          onClick={() => setExpandedStock(expandedStock === stock.symbol ? null : stock.symbol)}
                        >
                          <td className="py-3 px-3 sticky left-0 bg-neutral-800">
                            <div className="flex items-center space-x-2">
                              <div className="font-semibold text-white">{stock.symbol}</div>
                              {stock.cooldownFlag && (
                                <span className="w-2 h-2 bg-orange-400 rounded-full" title="In cooldown"></span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              stock.freshOrSticky === 'fresh' 
                                ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
                                : 'bg-blue-900/30 text-blue-400 border border-blue-500/30'
                            }`}>
                              {stock.freshOrSticky === 'fresh' ? 'NEW' : 'HOLD'}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <div className="text-neutral-300 text-xs">{stock.sector}</div>
                          </td>
                          <td className="py-3 px-2 text-white font-mono">${stock.lastPrice.toFixed(2)}</td>
                          <td className="py-3 px-2">
                            <div className="flex items-center space-x-2">
                              <div className="w-12 bg-neutral-600 rounded-full h-1.5">
                                <div 
                                  className={`h-1.5 rounded-full ${
                                    stock.compositeScore >= 0.7 ? 'bg-green-400' :
                                    stock.compositeScore >= 0.5 ? 'bg-cyan-400' :
                                    'bg-yellow-400'
                                  }`}
                                  style={{ width: `${Math.min(stock.compositeScore * 100, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-white text-xs font-mono">{(stock.compositeScore * 100).toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            {stock.scoreMomentum !== undefined ? (
                              <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                                stock.scoreMomentum > 0.01 ? 'bg-green-900/30 text-green-400' :
                                stock.scoreMomentum < -0.01 ? 'bg-red-900/30 text-red-400' :
                                'bg-neutral-700 text-neutral-400'
                              }`}>
                                {stock.scoreMomentum > 0 ? '+' : ''}{(stock.scoreMomentum * 100).toFixed(2)}%
                              </span>
                            ) : (
                              <span className="text-neutral-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {stock.relativeStrengthVsSPY !== undefined ? (
                              <span className={`font-mono text-xs ${
                                stock.relativeStrengthVsSPY > 0.55 ? 'text-green-400' :
                                stock.relativeStrengthVsSPY < 0.45 ? 'text-red-400' :
                                'text-neutral-400'
                              }`}>
                                {(stock.relativeStrengthVsSPY * 100).toFixed(0)}%
                              </span>
                            ) : (
                              <span className="text-neutral-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {stock.sectorPercentile !== undefined && stock.sectorPercentile > 0 ? (
                              <div className="flex items-center space-x-1">
                                <div className="w-8 bg-neutral-600 rounded-full h-1.5">
                                  <div 
                                    className={`h-1.5 rounded-full ${
                                      stock.sectorPercentile >= 0.8 ? 'bg-green-400' :
                                      stock.sectorPercentile >= 0.5 ? 'bg-yellow-400' :
                                      'bg-red-400'
                                    }`}
                                    style={{ width: `${stock.sectorPercentile * 100}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs text-neutral-400">{(stock.sectorPercentile * 100).toFixed(0)}</span>
                              </div>
                            ) : (
                              <span className="text-neutral-500 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {stock.consecutiveSessionsInUniverse !== undefined && stock.consecutiveSessionsInUniverse > 0 ? (
                              <span className={`text-xs font-mono ${
                                stock.consecutiveSessionsInUniverse >= 5 ? 'text-blue-400' :
                                stock.consecutiveSessionsInUniverse >= 3 ? 'text-cyan-400' :
                                'text-neutral-400'
                              }`}>
                                {stock.consecutiveSessionsInUniverse}x
                              </span>
                            ) : (
                              <span className="text-neutral-500 text-xs">new</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {stock.earningsZone ? (
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                stock.earningsZone === 'FAR' ? 'bg-green-900/30 text-green-400' :
                                stock.earningsZone === 'APPROACH' ? 'bg-yellow-900/30 text-yellow-400' :
                                stock.earningsZone === 'IMMINENT' ? 'bg-orange-900/30 text-orange-400' :
                                stock.earningsZone === 'BLACKOUT' ? 'bg-red-900/30 text-red-400' :
                                stock.earningsZone === 'POST_EARNINGS' ? 'bg-purple-900/30 text-purple-400' :
                                'bg-neutral-900/30 text-neutral-400'
                              }`}>
                                {stock.earningsZone === 'POST_EARNINGS' ? 'POST' : stock.earningsZone}
                              </span>
                            ) : (
                              <span className="text-neutral-500 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex space-x-1">
                              <span className="text-xs px-1 bg-neutral-700 rounded" title="Liquidity">
                                L:{(stock.liquidityScore * 100).toFixed(0)}
                              </span>
                              <span className="text-xs px-1 bg-neutral-700 rounded" title="Technical">
                                T:{(stock.technicalScore * 100).toFixed(0)}
                              </span>
                              <span className="text-xs px-1 bg-neutral-700 rounded" title="Sentiment">
                                S:{(stock.sentimentScore * 100).toFixed(0)}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            {stock.addedAtET ? (
                              <div className="text-xs text-neutral-300" title={stock.addedAt}>
                                {stock.addedAtET}
                              </div>
                            ) : (
                              <span className="text-neutral-500 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <div className="text-xs text-neutral-400 max-w-24 truncate" title={stock.reasons.join(', ')}>
                              {stock.reasons.slice(0, 1).join(', ')}
                              {stock.reasons.length > 1 && ` +${stock.reasons.length - 1}`}
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded Details Row */}
                        {expandedStock === stock.symbol && (
                          <tr className="bg-neutral-700/30">
                            <td colSpan={13} className="py-4 px-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                <div className="bg-neutral-800 rounded-lg p-3">
                                  <p className="text-xs text-neutral-400">Industry</p>
                                  <p className="text-sm text-white">{stock.industry}</p>
                                </div>
                                <div className="bg-neutral-800 rounded-lg p-3">
                                  <p className="text-xs text-neutral-400">Market Cap</p>
                                  <p className="text-sm text-white">${(stock.marketCap / 1e9).toFixed(2)}B</p>
                                </div>
                                <div className="bg-neutral-800 rounded-lg p-3">
                                  <p className="text-xs text-neutral-400">ADV (20d)</p>
                                  <p className="text-sm text-white">{(stock.adv20Shares / 1e6).toFixed(2)}M</p>
                                </div>
                                <div className="bg-neutral-800 rounded-lg p-3">
                                  <p className="text-xs text-neutral-400">Dollar Volume</p>
                                  <p className="text-sm text-white">${(stock.dollarAdv20 / 1e6).toFixed(1)}M</p>
                                </div>
                                <div className="bg-neutral-800 rounded-lg p-3">
                                  <p className="text-xs text-neutral-400">Spread</p>
                                  <p className="text-sm text-white">{stock.spreadBps.toFixed(1)} bps</p>
                                </div>
                                <div className="bg-neutral-800 rounded-lg p-3">
                                  <p className="text-xs text-neutral-400">Volatility Score</p>
                                  <p className="text-sm text-white">{(stock.volatilityScore * 100).toFixed(1)}%</p>
                                </div>
                                {stock.activityScore !== undefined && (
                                  <div className="bg-neutral-800 rounded-lg p-3">
                                    <p className="text-xs text-neutral-400">Activity Score</p>
                                    <p className="text-sm text-white">{(stock.activityScore * 100).toFixed(1)}%</p>
                                  </div>
                                )}
                                {stock.buzzScore !== undefined && (
                                  <div className="bg-neutral-800 rounded-lg p-3">
                                    <p className="text-xs text-neutral-400">Buzz Score</p>
                                    <p className="text-sm text-white">{(stock.buzzScore * 100).toFixed(1)}%</p>
                                  </div>
                                )}
                                {stock.sectorStrength !== undefined && (
                                  <div className="bg-neutral-800 rounded-lg p-3">
                                    <p className="text-xs text-neutral-400">Sector Strength</p>
                                    <p className={`text-sm ${stock.sectorStrength > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {stock.sectorStrength > 0 ? '+' : ''}{(stock.sectorStrength * 100).toFixed(2)}%
                                    </p>
                                  </div>
                                )}
                                <div className="bg-neutral-800 rounded-lg p-3 col-span-2 md:col-span-3">
                                  <p className="text-xs text-neutral-400">All Reasons</p>
                                  <p className="text-sm text-white">{stock.reasons.join(' | ')}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {filteredStocks.length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-neutral-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-neutral-400 text-lg">No stocks in universe</p>
                  <p className="text-neutral-500 text-sm mt-2">The trading universe will be populated during market hours</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'regime' && data && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Market Regime Analysis</h3>
              
              {/* Enhanced Regime Information */}
              {data.enhancedRegime && (
                <div className="mb-6">
                  {/* Regime Errors (if UNKNOWN) */}
                  {data.enhancedRegime.errors && data.enhancedRegime.errors.length > 0 && (
                    <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4">
                      <h4 className="text-red-300 font-semibold mb-2">⚠ Regime Calculation Errors</h4>
                      <ul className="space-y-1">
                        {data.enhancedRegime.errors.map((err, idx) => (
                          <li key={idx} className="text-sm text-red-200">
                            <strong>[{err.code}]</strong> {err.indicator}: {err.message}
                          </li>
                        ))}
                      </ul>
                      {data.enhancedRegime.behavior && (
                        <div className="mt-3 pt-3 border-t border-red-500/30 text-sm text-red-200">
                          <strong>System Behavior:</strong> Max new entries: {data.enhancedRegime.behavior.max_new_entries}, 
                          Exits: {data.enhancedRegime.behavior.exits_active ? 'Active' : 'Inactive'}, 
                          Monitoring: {data.enhancedRegime.behavior.monitoring}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-500/50 rounded-lg p-5 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded text-lg font-bold ${
                          data.enhancedRegime.regime_label === 'risk_on' ? 'bg-green-900/50 text-green-300' :
                          data.enhancedRegime.regime_label === 'risk_off' ? 'bg-red-900/50 text-red-300' :
                          data.enhancedRegime.regime_label === 'UNKNOWN' ? 'bg-gray-900/50 text-gray-300' :
                          'bg-yellow-900/50 text-yellow-300'
                        }`}>
                          {data.enhancedRegime.regime_label.toUpperCase().replace('_', '-')}
                        </span>
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          data.enhancedRegime.confidence === 'HIGH' ? 'bg-blue-900/50 text-blue-300' :
                          data.enhancedRegime.confidence === 'MEDIUM' ? 'bg-purple-900/50 text-purple-300' :
                          'bg-gray-900/50 text-gray-300'
                        }`}>
                          Confidence: {data.enhancedRegime.confidence}
                        </span>
                        {data.enhancedRegime.stability && (
                          <span className={`px-2 py-1 rounded text-sm font-medium ${
                            data.enhancedRegime.stability.isStable ? 'bg-green-900/50 text-green-300' : 'bg-orange-900/50 text-orange-300'
                          }`}>
                            {data.enhancedRegime.stability.isStable ? '✓ Stable' : '⚠ Unstable'}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">
                          {data.enhancedRegime.regime_score > 0 ? '+' : ''}{data.enhancedRegime.regime_score.toFixed(2)}
                        </div>
                        <div className="text-xs text-neutral-400">Composite Score</div>
                      </div>
                    </div>
                    {data.enhancedRegime.signals && data.enhancedRegime.signals.length > 0 && (
                      <div className="mt-3 text-sm text-neutral-200">
                        <strong>Active Signals:</strong>
                        <ul className="mt-1 space-y-1">
                          {data.enhancedRegime.signals.slice(0, 3).map((signal: string, idx: number) => (
                            <li key={idx} className="text-neutral-300">• {signal}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Formula Breakdown Section */}
                  <div className="bg-neutral-900/50 border border-cyan-500/30 rounded-lg p-5 mb-4">
                    <h4 className="text-lg font-semibold text-cyan-300 mb-3">Detection Formula</h4>
                    {data.regimeConfig ? (
                      <>
                        <div className="bg-neutral-800/80 p-3 rounded font-mono text-xs text-neutral-300 mb-4 overflow-x-auto">
                          composite_score = (volatility × {(data.regimeConfig.weights.volatility * 100).toFixed(0)}%) + (spy_trend × {(data.regimeConfig.weights.spyTrend * 100).toFixed(0)}%) + (breadth × {(data.regimeConfig.weights.breadth * 100).toFixed(0)}%) + (put_call × {(data.regimeConfig.weights.putCall * 100).toFixed(0)}%) + (credit × {(data.regimeConfig.weights.creditSpread * 100).toFixed(0)}%) + (economic × {(data.regimeConfig.weights.economic * 100).toFixed(0)}%)
                        </div>
                        <div className="text-sm text-neutral-400 mb-3">
                          <strong className="text-white">Source:</strong> <span className="font-mono text-xs">{data.regimeConfig.source}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="bg-neutral-800/80 p-3 rounded font-mono text-xs text-neutral-300 mb-4 overflow-x-auto">
                          composite_score = (volatility × 35%) + (spy_trend × 25%) + (breadth × 20%) + (put_call × 5%) + (credit × 10%) + (economic × 5%)
                        </div>
                        <div className="text-sm text-neutral-400 mb-3">
                          <strong className="text-white">Source:</strong> <span className="font-mono text-xs">Loading config...</span>
                        </div>
                      </>
                    )}
                    
                    {/* Weights and Contributions */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                      {data.enhancedRegime.indicators.volatility && data.regimeConfig && (
                        <div className="bg-neutral-800/50 p-2 rounded">
                          <div className="text-xs text-neutral-400">Volatility Weight</div>
                          <div className="text-lg font-bold text-yellow-400">{(data.regimeConfig.weights.volatility * 100).toFixed(0)}%</div>
                          <div className="text-xs text-neutral-500">
                            Score: {(data.enhancedRegime.indicators.volatility.score * 100).toFixed(0)}% →
                            <span className={data.enhancedRegime.indicators.volatility.score * data.regimeConfig.weights.volatility >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {' '}{(data.enhancedRegime.indicators.volatility.score * data.regimeConfig.weights.volatility > 0 ? '+' : '')}
                              {(data.enhancedRegime.indicators.volatility.score * data.regimeConfig.weights.volatility).toFixed(3)}
                            </span>
                          </div>
                        </div>
                      )}
                      {data.enhancedRegime.indicators.spyTrend && data.regimeConfig && (
                        <div className="bg-neutral-800/50 p-2 rounded">
                          <div className="text-xs text-neutral-400">SPY Trend Weight</div>
                          <div className="text-lg font-bold text-blue-400">{(data.regimeConfig.weights.spyTrend * 100).toFixed(0)}%</div>
                          <div className="text-xs text-neutral-500">
                            Score: {(data.enhancedRegime.indicators.spyTrend.score * 100).toFixed(0)}% →
                            <span className={data.enhancedRegime.indicators.spyTrend.score * data.regimeConfig.weights.spyTrend >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {' '}{(data.enhancedRegime.indicators.spyTrend.score * data.regimeConfig.weights.spyTrend > 0 ? '+' : '')}
                              {(data.enhancedRegime.indicators.spyTrend.score * data.regimeConfig.weights.spyTrend).toFixed(3)}
                            </span>
                          </div>
                        </div>
                      )}
                      {data.enhancedRegime.indicators.breadth && data.regimeConfig && (
                        <div className="bg-neutral-800/50 p-2 rounded">
                          <div className="text-xs text-neutral-400">Breadth Weight</div>
                          <div className="text-lg font-bold text-purple-400">{(data.regimeConfig.weights.breadth * 100).toFixed(0)}%</div>
                          <div className="text-xs text-neutral-500">
                            Score: {(data.enhancedRegime.indicators.breadth.score * 100).toFixed(0)}% →
                            <span className={data.enhancedRegime.indicators.breadth.score * data.regimeConfig.weights.breadth >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {' '}{(data.enhancedRegime.indicators.breadth.score * data.regimeConfig.weights.breadth > 0 ? '+' : '')}
                              {(data.enhancedRegime.indicators.breadth.score * data.regimeConfig.weights.breadth).toFixed(3)}
                            </span>
                          </div>
                        </div>
                      )}
                      {data.enhancedRegime.indicators.putCall && data.regimeConfig && (
                        <div className="bg-neutral-800/50 p-2 rounded">
                          <div className="text-xs text-neutral-400">Put/Call Weight</div>
                          <div className="text-lg font-bold text-pink-400">{(data.regimeConfig.weights.putCall * 100).toFixed(0)}%</div>
                          <div className="text-xs text-neutral-500">
                            Score: {(data.enhancedRegime.indicators.putCall.score * 100).toFixed(0)}% →
                            <span className={data.enhancedRegime.indicators.putCall.score * data.regimeConfig.weights.putCall >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {' '}{(data.enhancedRegime.indicators.putCall.score * data.regimeConfig.weights.putCall > 0 ? '+' : '')}
                              {(data.enhancedRegime.indicators.putCall.score * data.regimeConfig.weights.putCall).toFixed(3)}
                            </span>
                          </div>
                        </div>
                      )}
                      {data.enhancedRegime.indicators.creditSpread && data.regimeConfig && (
                        <div className="bg-neutral-800/50 p-2 rounded">
                          <div className="text-xs text-neutral-400">Credit Weight</div>
                          <div className="text-lg font-bold text-cyan-400">{(data.regimeConfig.weights.creditSpread * 100).toFixed(0)}%</div>
                          <div className="text-xs text-neutral-500">
                            Score: {(data.enhancedRegime.indicators.creditSpread.score * 100).toFixed(0)}% →
                            <span className={data.enhancedRegime.indicators.creditSpread.score * data.regimeConfig.weights.creditSpread >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {' '}{(data.enhancedRegime.indicators.creditSpread.score * data.regimeConfig.weights.creditSpread > 0 ? '+' : '')}
                              {(data.enhancedRegime.indicators.creditSpread.score * data.regimeConfig.weights.creditSpread).toFixed(3)}
                            </span>
                          </div>
                        </div>
                      )}
                      {data.enhancedRegime.indicators.economic && data.regimeConfig && (
                        <div className="bg-neutral-800/50 p-2 rounded">
                          <div className="text-xs text-neutral-400">Economic Weight</div>
                          <div className="text-lg font-bold text-green-400">{(data.regimeConfig.weights.economic * 100).toFixed(0)}%</div>
                          <div className="text-xs text-neutral-500">
                            Score: {(data.enhancedRegime.indicators.economic.score * 100).toFixed(0)}% →
                            <span className={data.enhancedRegime.indicators.economic.score * data.regimeConfig.weights.economic >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {' '}{(data.enhancedRegime.indicators.economic.score * data.regimeConfig.weights.economic > 0 ? '+' : '')}
                              {(data.enhancedRegime.indicators.economic.score * data.regimeConfig.weights.economic).toFixed(3)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Thresholds */}
                    {data.regimeConfig && (
                      <div className="border-t border-neutral-700 pt-3 mt-3">
                        <div className="text-sm font-medium text-neutral-300 mb-2">Regime Thresholds:</div>
                        <div className="flex gap-4 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-red-900/30 text-red-400 rounded">Risk Off</span>
                            <span className="text-neutral-400">score ≤ {data.regimeConfig.thresholds.risk_off.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-yellow-900/30 text-yellow-400 rounded">Neutral</span>
                            <span className="text-neutral-400">{data.regimeConfig.thresholds.risk_off.toFixed(2)} &lt; score &lt; {data.regimeConfig.thresholds.risk_on.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded">Risk On</span>
                            <span className="text-neutral-400">score ≥ {data.regimeConfig.thresholds.risk_on.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 5 Core Indicators Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Realized Volatility Indicator */}
                    <div className="bg-neutral-700 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-neutral-400 mb-2">1. Realized Volatility (SPY RV20)</h4>
                      {data.enhancedRegime.indicators.volatility ? (
                        <>
                          <div className="flex items-baseline justify-between">
                            <p className="text-2xl font-bold text-yellow-400">{data.enhancedRegime.indicators.volatility.rv20.toFixed(1)}%</p>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              data.enhancedRegime.indicators.volatility.state === 'STRESS' ? 'bg-red-900/50 text-red-300' :
                              data.enhancedRegime.indicators.volatility.state === 'HIGH' ? 'bg-orange-900/50 text-orange-300' :
                              data.enhancedRegime.indicators.volatility.state === 'NORMAL' ? 'bg-green-900/50 text-green-300' :
                              'bg-blue-900/50 text-blue-300'
                            }`}>
                              {data.enhancedRegime.indicators.volatility.state}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-neutral-400">
                            Z-Score: {data.enhancedRegime.indicators.volatility.zScore.toFixed(2)} | 
                            Percentile: {data.enhancedRegime.indicators.volatility.percentile.toFixed(0)}% | 
                            Score: {(data.enhancedRegime.indicators.volatility.score * 100).toFixed(0)}%
                            {data.enhancedRegime.indicators.volatility.vxxStress && (
                              <span className="ml-2 text-red-400">⚠ VXX Stress</span>
                            )}
                          </div>
                        </>
                      ) : (
                        <p className="text-red-400 text-sm">FAILED - No data available</p>
                      )}
                    </div>

                    {/* SPY Trend Indicator */}
                    <div className="bg-neutral-700 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-neutral-400 mb-2">2. SPY Trend</h4>
                      <div className="flex items-baseline justify-between">
                        <p className="text-2xl font-bold text-white">${data.enhancedRegime.indicators.spyTrend?.price != null ? data.enhancedRegime.indicators.spyTrend.price.toFixed(0) : 'N/A'}</p>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          data.enhancedRegime.indicators.spyTrend?.trend === 'STRONG_UP' ? 'bg-green-900/50 text-green-300' :
                          data.enhancedRegime.indicators.spyTrend?.trend === 'UP' ? 'bg-green-900/30 text-green-400' :
                          data.enhancedRegime.indicators.spyTrend?.trend === 'DOWN' ? 'bg-red-900/30 text-red-400' :
                          data.enhancedRegime.indicators.spyTrend?.trend === 'STRONG_DOWN' ? 'bg-red-900/50 text-red-300' :
                          'bg-yellow-900/50 text-yellow-300'
                        }`}>
                          {data.enhancedRegime.indicators.spyTrend?.trend?.replace('_', ' ') || 'N/A'}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-neutral-400">
                        Score: {data.enhancedRegime.indicators.spyTrend?.score != null ? (data.enhancedRegime.indicators.spyTrend.score * 100).toFixed(0) : 'N/A'}% | 
                        {data.enhancedRegime.indicators.spyTrend?.goldenCross ? ' Golden Cross' : 
                         data.enhancedRegime.indicators.spyTrend?.deathCross ? ' Death Cross' : ' No Cross'}
                      </div>
                    </div>

                    {/* Market Breadth */}
                    <div className="bg-neutral-700 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-neutral-400 mb-2">3. Market Breadth (RSP/SPY)</h4>
                      <div className="flex items-baseline justify-between">
                        <p className="text-2xl font-bold text-white">
                          {data.enhancedRegime.indicators.breadth?.rspSpyRatio != null ? 
                            data.enhancedRegime.indicators.breadth.rspSpyRatio.toFixed(4) : 
                            (data.enhancedRegime.indicators.breadth?.advanceDeclineRatio != null ? 
                              data.enhancedRegime.indicators.breadth.advanceDeclineRatio.toFixed(2) : 'N/A')}
                        </p>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          data.enhancedRegime.indicators.breadth?.signal === 'STRONG_BULLISH' ? 'bg-green-900/50 text-green-300' :
                          data.enhancedRegime.indicators.breadth?.signal === 'BULLISH' ? 'bg-green-900/30 text-green-400' :
                          data.enhancedRegime.indicators.breadth?.signal === 'BEARISH' ? 'bg-red-900/30 text-red-400' :
                          data.enhancedRegime.indicators.breadth?.signal === 'STRONG_BEARISH' ? 'bg-red-900/50 text-red-300' :
                          'bg-yellow-900/50 text-yellow-300'
                        }`}>
                          {data.enhancedRegime.indicators.breadth?.signal?.replace('_', ' ') || 'N/A'}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-neutral-400">
                        {data.enhancedRegime.indicators.breadth?.zScore != null ? (
                          <>Z-Score: {data.enhancedRegime.indicators.breadth.zScore.toFixed(2)} | </>
                        ) : null}
                        Score: {data.enhancedRegime.indicators.breadth?.score != null ? (data.enhancedRegime.indicators.breadth.score * 100).toFixed(0) : 'N/A'}% | 
                        Above 50d: {data.enhancedRegime.indicators.breadth?.percentAbove50dma != null ? data.enhancedRegime.indicators.breadth.percentAbove50dma.toFixed(0) : 'N/A'}%
                      </div>
                    </div>

                    {/* Credit Spreads */}
                    <div className="bg-neutral-700 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-neutral-400 mb-2">4. Credit Spreads (HYG/LQD)</h4>
                      <div className="flex items-baseline justify-between">
                        <p className="text-2xl font-bold text-cyan-400">{data.enhancedRegime.indicators.creditSpread?.spreadRatio != null ? data.enhancedRegime.indicators.creditSpread.spreadRatio.toFixed(3) : 'N/A'}</p>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          data.enhancedRegime.indicators.creditSpread?.signal === 'RISK_ON' ? 'bg-green-900/50 text-green-300' :
                          data.enhancedRegime.indicators.creditSpread?.signal === 'RISK_OFF' ? 'bg-red-900/50 text-red-300' :
                          data.enhancedRegime.indicators.creditSpread?.signal === 'STRESS' ? 'bg-red-900/70 text-red-200' :
                          'bg-yellow-900/50 text-yellow-300'
                        }`}>
                          {data.enhancedRegime.indicators.creditSpread?.signal?.replace('_', ' ') || 'N/A'}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-neutral-400">
                        {data.enhancedRegime.indicators.creditSpread?.zScore != null ? (
                          <>Z-Score: {data.enhancedRegime.indicators.creditSpread.zScore.toFixed(2)} | </>
                        ) : null}
                        Score: {data.enhancedRegime.indicators.creditSpread?.score != null ? (data.enhancedRegime.indicators.creditSpread.score * 100).toFixed(0) : 'N/A'}% | 
                        HYG/LQD Ratio
                      </div>
                    </div>

                    {/* Economic Indicators */}
                    <div className="bg-neutral-700 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-neutral-400 mb-2">5. Economic (FRED)</h4>
                      <div className="flex items-baseline justify-between">
                        <p className="text-2xl font-bold text-green-400">{data.enhancedRegime.indicators.economic?.unemploymentRate != null ? data.enhancedRegime.indicators.economic.unemploymentRate.toFixed(1) : 'N/A'}%</p>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          data.enhancedRegime.indicators.economic?.signal === 'EXPANSION' ? 'bg-green-900/50 text-green-300' :
                          data.enhancedRegime.indicators.economic?.signal === 'CONTRACTION' ? 'bg-orange-900/50 text-orange-300' :
                          data.enhancedRegime.indicators.economic?.signal === 'RECESSION_WARNING' ? 'bg-red-900/50 text-red-300' :
                          'bg-yellow-900/50 text-yellow-300'
                        }`}>
                          {data.enhancedRegime.indicators.economic?.signal?.replace('_', ' ') || 'N/A'}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-neutral-400">
                        Score: {data.enhancedRegime.indicators.economic?.score != null ? (data.enhancedRegime.indicators.economic.score * 100).toFixed(0) : 'N/A'}% | 
                        Yield Curve: {data.enhancedRegime.indicators.economic?.yieldCurve != null ? ((data.enhancedRegime.indicators.economic.yieldCurve > 0 ? '+' : '') + data.enhancedRegime.indicators.economic.yieldCurve.toFixed(2)) : 'N/A'}%
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 text-xs text-neutral-400 text-center">
                    Enhanced Regime Detection v3.0 (5 Core Indicators) • Updated: {new Date(data.enhancedRegime.timestamp).toLocaleString()}
                  </div>
                </div>
              )}

              {/* Legacy Regime Display (fallback if enhanced not available) */}
              {!data.enhancedRegime && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-neutral-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-white mb-2">VIX Level</h4>
                    <p className="text-3xl font-bold text-yellow-400">{data.regime.vix.toFixed(2)}</p>
                    <p className="text-sm text-neutral-400 mt-1">
                      {data.regime.vix > 25 ? 'High Volatility (Risk-Off)' : 
                       data.regime.vix < 15 ? 'Low Volatility (Risk-On)' : 'Moderate Volatility'}
                    </p>
                  </div>
                  
                  <div className="bg-neutral-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-white mb-2">SPY vs 20-day MA</h4>
                    {(() => {
                      const v = data.regime.spy_ma_20_pct;
                      const normalized = v > 50 ? v / 100 : v;
                      return (
                        <p className={`text-3xl font-bold ${normalized > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {normalized > 0 ? '+' : ''}{normalized.toFixed(2)}%
                        </p>
                      );
                    })()}
                    <p className="text-sm text-neutral-400 mt-1">
                      {(data.regime.spy_ma_20_pct > 50 ? data.regime.spy_ma_20_pct / 100 : data.regime.spy_ma_20_pct) > 0 ? 'Above 20-day MA' : 'Below 20-day MA'}
                    </p>
                  </div>
                  
                  <div className="bg-neutral-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-white mb-2">SPY vs 50-day MA</h4>
                    {(() => {
                      const v = data.regime.spy_ma_50_pct;
                      const normalized = v > 50 ? v / 100 : v;
                      return (
                        <p className={`text-3xl font-bold ${normalized > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {normalized > 0 ? '+' : ''}{normalized.toFixed(2)}%
                        </p>
                      );
                    })()}
                    <p className="text-sm text-neutral-400 mt-1">
                      {(data.regime.spy_ma_50_pct > 50 ? data.regime.spy_ma_50_pct / 100 : data.regime.spy_ma_50_pct) > 0 ? 'Above 50-day MA' : 'Below 50-day MA'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'qa' && data && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">QA Acceptance Check Results</h3>
              <div className="mb-6 bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border border-cyan-500/50 rounded-lg p-5">
                <div className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                  <div>
                    <p className="text-cyan-300 font-semibold">Coming Soon</p>
                    <p className="text-neutral-300 text-sm">
                      QA checks are not yet wired to trading and are for display only. They do not affect selection or execution.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {data.qaResults.map((result: any) => (
                  <div key={result.ruleId} className="bg-neutral-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-lg font-semibold text-white">{result.ruleId}</h4>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          result.passed ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                        }`}>
                          {result.passed ? 'PASSED' : 'FAILED'}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(result.severity)}`}>
                          {result.severity.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <p className="text-neutral-300 mb-2">{result.message}</p>
                    <div className="text-sm text-neutral-400">
                      <p>Expected: {JSON.stringify(result.expected)}</p>
                      <p>Actual: {JSON.stringify(result.actual)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingUniverse;
